import { Injectable } from '@angular/core';
import { BaseEquipmentService } from './base-equipment.service';
import {
  SolarStorage,
  CreateStorageDto,
  UpdateStorageDto,
  EquipmentListResponse,
} from '@features/catalog/models';

/**
 * Storage Service
 * Handles all storage-related API operations
 * Endpoint: /organizations/{orgId}/equipment/storages
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService extends BaseEquipmentService<
  SolarStorage,
  CreateStorageDto,
  UpdateStorageDto
> {
  private readonly equipmentType = 'storages' as const;

  /**
   * Get all storages for an organization
   */
  async getStorages(
    organizationId: string
  ): Promise<EquipmentListResponse<SolarStorage>> {
    return this.getAll(organizationId, this.equipmentType);
  }

  /**
   * Get a specific storage by ID
   */
  async getStorageById(
    organizationId: string,
    storageId: string
  ): Promise<SolarStorage> {
    return this.getById(organizationId, this.equipmentType, storageId);
  }

  /**
   * Create a new storage
   */
  async createStorage(
    organizationId: string,
    data: CreateStorageDto
  ): Promise<SolarStorage> {
    return this.create(organizationId, this.equipmentType, data);
  }

  /**
   * Update an existing storage
   */
  async updateStorage(
    organizationId: string,
    storageId: string,
    data: UpdateStorageDto
  ): Promise<SolarStorage> {
    return this.update(organizationId, this.equipmentType, storageId, data);
  }

  /**
   * Delete a storage (soft delete)
   */
  async deleteStorage(
    organizationId: string,
    storageId: string
  ): Promise<void> {
    return this.delete(organizationId, this.equipmentType, storageId);
  }

  /**
   * Sync global storages from Solar Insight
   */
  async syncGlobalStorages(organizationId: string): Promise<SolarStorage[]> {
    return this.sync<SolarStorage[]>(organizationId, this.equipmentType);
  }
}
